import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Client, CommandStatus } from '../../../../../../../../api/api-reference';
import { CommonModule, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelectDropdownSearchComponent } from '../../../../../multi-select-dropdown-search/multi-select-dropdown-search.component';
import { FilterPanelComponent } from '../../../filter-panel.component';

export interface FirmwareUpdateGroupDetailsFilterOutput {
  assetTypeIds?: string[];
  areas?: string[];
  regions?: string[];
  statuses?: CommandStatus[];
}

@Component({
  selector: 'app-firmware-update-group-details-filter',
  standalone: true,
  imports: [CommonModule, NgFor, TranslateModule, FilterPanelComponent, MultiSelectDropdownSearchComponent],
  templateUrl: './firmware-update-group-details-filter.component.html',
  styleUrl: './firmware-update-group-details-filter.component.scss'
})
export class FirmwareUpdateGroupDetailsFilterComponent {
  @Input() numberOfResults: number = 0;
  @Input() selectedAssetTypeIds: string[] = [];
  @Input() selectedAreas: string[] = [];
  @Input() selectedRegions: string[] = [];
  @Input() selectedStatuses?: CommandStatus[];

  @Output() applied = new EventEmitter<FirmwareUpdateGroupDetailsFilterOutput>();

  readonly statuses = Object.values(CommandStatus);

  private readonly statusDisplayTranslationKeys: Record<CommandStatus, string> = {
    [CommandStatus.Pending]: 'PENDING',
    [CommandStatus.Success]: 'SUCCESSFUL',
    [CommandStatus.Failed]: 'FAILED',
    [CommandStatus.InProgress]: 'INPROGRESS',
    [CommandStatus.Finalize]: 'FINALIZE',
    [CommandStatus.Canceled]: 'CANCELED',
    [CommandStatus.ErrorOccured]: 'FATAL_ERROR',
    [CommandStatus.Scheduled]: 'SCHEDULED',
  };

  draftAssetTypeIds: string[] = [];
  draftAreas: string[] = [];
  draftRegions: string[] = [];
  draftStatuses: CommandStatus[] = [];
  fetchAssetTypes = () => this.client.getAllAssetTypesForOptions(undefined);
  fetchAreas = () => this.client.getAllAreasForOptions();
  fetchRegions = () => this.client.getAllRegionsForOptions();

  get activeFilterCount(): number {
    return (
      this.selectedAssetTypeIds.length +
      this.selectedAreas.length +
      this.selectedRegions.length +
      (this.selectedStatuses?.length ?? 0)
    );
  }

  constructor(private client: Client) { }

  ngOnInit(): void {
    this.reset();
  }

  onAssetTypeListReceived(list: string[]): void {
    this.draftAssetTypeIds = list;
  }

  onAreaListReceived(list: string[]): void {
    this.draftAreas = list;
  }

  onRegionListReceived(list: string[]): void {
    this.draftRegions = list;
  }

  toggleStatus(status: CommandStatus): void {
    this.draftStatuses = this.draftStatuses.includes(status)
      ? this.draftStatuses.filter((item) => item !== status)
      : [...this.draftStatuses, status];
  }

  statusTranslationKey(status: CommandStatus): string {
    return this.statusDisplayTranslationKeys[status] ?? status.toUpperCase();
  }

  onApply(): void {
    this.applied.emit({
      assetTypeIds: this.draftAssetTypeIds,
      areas: this.draftAreas,
      regions: this.draftRegions,
      statuses: this.draftStatuses.length ? this.draftStatuses : undefined,
    });
  }

  onCancel(): void {
    this.reset();
  }

  private reset(): void {
    this.draftAssetTypeIds = [...this.selectedAssetTypeIds];
    this.draftAreas = [...this.selectedAreas];
    this.draftRegions = [...this.selectedRegions];
    this.draftStatuses = [...(this.selectedStatuses ?? [])];
  }
}
